import { Injectable, signal } from '@angular/core';

export interface Assignment {
  productId: string;
  stepIds: string[];
}

export interface UserProfile {
  name: string;
  username: string;
  // Roles are now admin-managed (see Manage Roles), so beyond the built-in 'admin'/'supervisor'
  // are just role names looked up dynamically — no longer a fixed union of literals.
  role: string;
  language: 'en' | 'mr';
  assignments: Assignment[];
  functionalities: string[];
}

const PROFILE_STORAGE_KEY = 'vw_user_profile';

// vwappservices' VwAppStateService only tracks the access token — this holds the rest of the
// profile (role/name/language/assignments) that the backend's login response is extended with.
@Injectable({ providedIn: 'root' })
export class AppStateService {
  profile = signal<UserProfile | null>(null);

  setProfile(profile: UserProfile) {
    this.profile.set(profile);
    localStorage.setItem(PROFILE_STORAGE_KEY, JSON.stringify(profile));
  }

  restoreProfile(): UserProfile | null {
    const value = localStorage.getItem(PROFILE_STORAGE_KEY);
    if (!value) return null;
    const profile = JSON.parse(value) as UserProfile;
    this.profile.set(profile);
    return profile;
  }

  clearProfile() {
    this.profile.set(null);
    localStorage.removeItem(PROFILE_STORAGE_KEY);
  }

  isAdmin(): boolean {
    return this.profile()?.role === 'admin';
  }

  isAdminOrSupervisor(): boolean {
    const role = this.profile()?.role;
    return role === 'admin' || role === 'supervisor';
  }

  // Menu/route gating for custom (non-built-in) roles, driven by the functionality codes the
  // role was granted in Manage Roles.
  hasAccess(fncode: string): boolean {
    return this.profile()?.functionalities?.includes(fncode) ?? false;
  }
}
